// API Configuration - Using RiskAPI backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5068/api';

// ── Token store — populated by AuthContext when the Keycloak token arrives ──
let _authToken = null;

/**
 * Called by the AuthProvider (or App.js) whenever the Keycloak access token
 * is received or updated. All subsequent apiFetch calls will include it
 * as a Bearer header.
 */
export const setApiAuthToken = (token) => {
  _authToken = token;
};

/** Returns the current auth token (if any). */
export const getApiAuthToken = () => _authToken;

// ============ AUTH API ============
export const authApi = {
  /**
   * Get current user info from API (decodes JWT on server)
   * Returns: { isAuthenticated, role, roles, userId, userName, email }
   */
  getCurrentUser: () => apiFetch('/Auth/me'),
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }
  // Handle 204 No Content responses (e.g., from DELETE)
  if (response.status === 204) {
    return { success: true };
  }
  return response.json();
};

// Helper function to build query string
const buildQueryString = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

// Generic fetch wrapper with error handling and automatic Bearer auth
const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  // Attach Keycloak Bearer token if available
  if (_authToken) {
    defaultHeaders['Authorization'] = `Bearer ${_authToken}`;
  }

  const mergedOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, mergedOptions);
    return handleResponse(response);
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
};

// ============ ASSESSMENTS API ============
export const assessmentsApi = {
  getAll: (page = 1, pageSize = 10, type = null, status = null) => {
    const query = buildQueryString({ page, pageSize, type, status });
    return apiFetch(`/Assessments?${query}`);
  },
  
  getById: (id) => apiFetch(`/Assessments/${id}`),
  
  create: (data) => apiFetch('/Assessments', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiFetch(`/Assessments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiFetch(`/Assessments/${id}`, {
    method: 'DELETE',
  }),

  // Related records that will also be removed when the assessment is deleted
  getReferences: (id) => apiFetch(`/Assessments/${id}/references`),

  // Revoke (unshare) an assessment that was shared to the current user
  revoke: (id, userId = null) => {
    const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiFetch(`/Assessments/${id}/revoke${query}`, {
      method: 'POST',
    });
  },

  // Revoke all shared access for an assessment (any org admin can do this)
  revokeAll: (id) => {
    return apiFetch(`/Assessments/${id}/revoke-all`, {
      method: 'POST',
    });
  },

  // Get share details (orgs/users shared with)
  getShareDetails: (id) => apiFetch(`/Assessments/${id}/share-details`),

  // Revoke access for selected users
  revokeSelected: (id, userIds) => apiFetch(`/Assessments/${id}/revoke-selected`, {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  }),

  // Lookup endpoints
  getIndustries: () => apiFetch('/Assessments/lookups/industries'),
  getDepartments: () => apiFetch('/Assessments/lookups/departments'),
  getDomains: () => apiFetch('/Assessments/lookups/domains'),
  getFocusAreas: () => apiFetch('/Assessments/lookups/focus-areas'),
  getDisciplines: () => apiFetch('/Assessments/lookups/disciplines'),
  getCategories: () => apiFetch('/Assessments/lookups/categories'),
  getVendors: () => apiFetch('/Assessments/lookups/vendors'),
  getProjects: () => apiFetch('/Assessments/lookups/projects'),

  // Share assessments with an organization and multiple users
  share: (assessmentIds, organizationId, userIds, ispublic = false) => apiFetch('/Assessments/share', {
    method: 'POST',
    body: JSON.stringify({ assessmentIds, organizationId, userIds, ispublic }),
  }),

  // Import assessments - Step 1: Parse file and return preview rows
  importParse: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${API_BASE_URL}/Assessments/import/parse`;
    const headers = { 'Accept': 'application/json' };
    if (_authToken) {
      headers['Authorization'] = `Bearer ${_authToken}`;
    }
    return fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    }).then(handleResponse);
  },

  // Import assessments - Step 2: Save confirmed (selected) rows
  importSave: (confirmedRows) => apiFetch('/Assessments/import/save', {
    method: 'POST',
    body: JSON.stringify(confirmedRows),
  }),

  // Get assessments filtered by organization + shared assessments for the user
  getMyAssessments: (page = 1, pageSize = 10, status = null, organizationName = null, userId = null) => {
    const query = buildQueryString({ page, pageSize, status, organizationName, userId });
    return apiFetch(`/Assessments/my?${query}`);
  },

  // Get assessments shared with a user by their email address
  getSharedAssessmentsByEmail: (email, page = 1, pageSize = 10, status = null) => {
    const query = buildQueryString({ email, page, pageSize, status });
    return apiFetch(`/Assessments/shared?${query}`);
  },

  // Get user dashboard: assigned assessments with questionnaire progress
  // renamed to CountInfo - returns counts/metrics for assessments assigned to a user
  CountInfo: (userId) => {
    const qs = `userId=${encodeURIComponent(userId)}`;
    return apiFetch(`/Assessments/CountInfo?${qs}`);
  },

  // Two-way acceptance
  reviewerAccept: (assessmentId, reviewerEmail = null) =>
    apiFetch(`/Assessments/${assessmentId}/reviewer-accept`, {
      method: 'POST',
      body: JSON.stringify({ reviewerEmail }),
    }),

  userAccept: (assessmentId, userEmail = null) =>
    apiFetch(`/Assessments/${assessmentId}/user-accept`, {
      method: 'POST',
      body: JSON.stringify({ userEmail }),
    }),

  userReject: (assessmentId, userEmail = null) =>
    apiFetch(`/Assessments/${assessmentId}/user-reject`, {
      method: 'POST',
      body: JSON.stringify({ userEmail }),
    }),

  getAcceptanceStatus: (assessmentId, userId) =>
    apiFetch(`/Assessments/${assessmentId}/acceptance-status${userId ? `?userId=${userId}` : ''}`),
};

// ============ DOCUMENTS API ============
export const documentsApi = {
  getAll: (page = 1, pageSize = 10) => {
    const query = buildQueryString({ page, pageSize });
    return apiFetch(`/Documents?${query}`);
  },

  getById: (id) => apiFetch(`/Documents/${id}`),

  // Fetch documents shared with a specific user (by userId and/or email)
  getForUser: (userId = null, email = null, page = 1, pageSize = 100) => {
    const query = buildQueryString({ sharedWithUserId: userId, sharedWithUserEmail: email, page, pageSize });
    return apiFetch(`/Documents?${query}`);
  },

  // Download a document. Returns { blob, filename } for binary streams or
  // { redirectUrl } when the backend returns a signed (SAS) URL as JSON.
  download: async (id) => {
    const url = `${API_BASE_URL}/Documents/${id}/download`;
    const headers = { Accept: '*/*' };
    if (_authToken) headers['Authorization'] = `Bearer ${_authToken}`;
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const contentType = res.headers.get('content-type') || '';
    // Backend may return a JSON payload containing a signed/SAS URL
    if (contentType.includes('application/json')) {
      const json = await res.json();
      const signedUrl = json.downloadUrl || json.sasUrl || json.url || json.fileUrl;
      if (signedUrl) return { redirectUrl: signedUrl };
      throw new Error('No download URL returned by server');
    }
    // Otherwise the backend is streaming the file binary directly
    const disposition = res.headers.get('content-disposition') || '';
    const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/i);
    const filename = match ? match[2].trim() : `document-${id}`;
    const blob = await res.blob();
    return { blob, filename };
  },

  create: (formData) => {
    const url = `${API_BASE_URL}/Documents`;
    const headers = { Accept: 'application/json' };
    if (_authToken) headers['Authorization'] = `Bearer ${_authToken}`;
    return fetch(url, { method: 'POST', headers, body: formData }).then(handleResponse);
  },

  update: (id, formData) => {
    const url = `${API_BASE_URL}/Documents/${id}`;
    const headers = { Accept: 'application/json' };
    if (_authToken) headers['Authorization'] = `Bearer ${_authToken}`;
    return fetch(url, { method: 'PUT', headers, body: formData }).then(handleResponse);
  },

  delete: (id) => apiFetch(`/Documents/${id}`, { method: 'DELETE' }),
};

// ============ ORGANIZATIONS API ============
export const organizationsApi = {
  getAll: (page = 1, pageSize = 10, status = null, category = null, search = null, deactivated = false) => {
    const query = buildQueryString({ page, pageSize, status, category, search, deactivated });
    return apiFetch(`/Organizations?${query}`);
  },

  getById: (id) => apiFetch(`/Organizations/${id}`),
  
  create: (data) => apiFetch('/Organizations', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiFetch(`/Organizations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiFetch(`/Organizations/${id}`, {
    method: 'DELETE',
  }),

  restore: (id) => apiFetch(`/Organizations/${id}/restore`, {
    method: 'POST',
  }),

  toggleTrustCenter: (id, enabled) => apiFetch(`/Organizations/${id}/trust-center`, {
    method: 'PATCH',
    body: JSON.stringify({ trustCenterEnabled: enabled }),
  }),
};

// ============ LOOKUPS API ============
export const lookupsApi = {
  // Industries
  getIndustries: () => apiFetch('/Lookups/industries'),
  createIndustry: (data) => apiFetch('/Lookups/industries', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateIndustry: (id, data) => apiFetch(`/Lookups/industries/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteIndustry: (id) => apiFetch(`/Lookups/industries/${id}`, {
    method: 'DELETE',
  }),

  // Segments
  getSegments: (industryId = null) => {
    const query = industryId ? `?industryId=${industryId}` : '';
    return apiFetch(`/Lookups/segments${query}`);
  },
  createSegment: (data) => apiFetch('/Lookups/segments', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateSegment: (id, data) => apiFetch(`/Lookups/segments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteSegment: (id) => apiFetch(`/Lookups/segments/${id}`, {
    method: 'DELETE',
  }),

  // Versions
  getVersions: () => apiFetch('/Lookups/versions'),
  createVersion: (data) => apiFetch('/Lookups/versions', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateVersion: (id, data) => apiFetch(`/Lookups/versions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteVersion: (id) => apiFetch(`/Lookups/versions/${id}`, {
    method: 'DELETE',
  }),

  // Options (dropdown values)
  getOptions: () => apiFetch('/Lookups/options'),
};

// ============ USERS API ============
export const usersApi = {
  baseURL: API_BASE_URL,
  getHeaders: () => ({
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }),
  
  getAll: (page = 1, pageSize = 10, status = null, role = null, search = null, filters = {}) => {
    const query = buildQueryString({ page, pageSize, status, role, search, ...filters });
    return apiFetch(`/Users?${query}`);
  },
  
  getStats: () => apiFetch('/Users/stats'),
  
  getById: (id) => apiFetch(`/Users/${id}`),
  
  create: (data) => apiFetch('/Users', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiFetch(`/Users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiFetch(`/Users/${id}`, {
    method: 'DELETE',
  }),
  
  // Export endpoints - all logic handled by backend
  exportCSV: async (searchTerm = '') => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    
    const response = await fetch(`${API_BASE_URL}/Users/export/csv?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/csv',
      },
    });
    
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },
  
  exportExcel: async (searchTerm = '') => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    
    const response = await fetch(`${API_BASE_URL}/Users/export/excel?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
    
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },

  // Password management endpoint
  setPassword: (userId, password) => apiFetch(`/Users/${userId}/set-password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  }),

  // Role management endpoints
  getAllRoles: () => apiFetch('/Users/roles'),

  getUserRoles: (userId) => apiFetch(`/Users/${userId}/roles`),

  assignRoles: (userId, roles) => apiFetch(`/Users/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify(roles),
  }),
};

// ============ QUESTIONNAIRES API ============
export const questionnairesApi = {
  getAll: (page = 1, pageSize = 10, status = null, categoryId = null, assessmentId = null) => {
    const query = buildQueryString({ page, pageSize, status, categoryId, assessmentId });
    return apiFetch(`/Questionnaires?${query}`);
  },
  
  getById: (id) => apiFetch(`/Questionnaires/${id}`),
  
  create: (data, assessmentId = null) => {
    const query = buildQueryString({ assessmentId });
    return apiFetch(`/Questionnaires${query ? `?${query}` : ''}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  // create: (data) => apiFetch('/Questionnaires', {
  //   method: 'POST',
  //   body: JSON.stringify(data),
  //   parameters: {assessmentId:assessmentId}
  // }),
  
  update: (id, data) => apiFetch(`/Questionnaires/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiFetch(`/Questionnaires/${id}`, {
    method: 'DELETE',
  }),
  
  // Questions
  getQuestions: (questionnaireId) => apiFetch(`/Questionnaires/${questionnaireId}/questions`),
  
  getQuestion: (questionnaireId, questionId) => apiFetch(`/Questionnaires/${questionnaireId}/questions/${questionId}`),
  
  createQuestion: (questionnaireId, data) => apiFetch(`/Questionnaires/${questionnaireId}/questions`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateQuestion: (questionnaireId, questionId, data) => apiFetch(`/Questionnaires/${questionnaireId}/questions/${questionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  deleteQuestion: (questionnaireId, questionId) => apiFetch(`/Questionnaires/${questionnaireId}/questions/${questionId}`, {
    method: 'DELETE',
  }),

  // Import questionnaires - Step 1: Parse file and return preview rows
  importParse: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${API_BASE_URL}/Questionnaires/import/parse`;
    const headers = { 'Accept': 'application/json' };
    if (_authToken) {
      headers['Authorization'] = `Bearer ${_authToken}`;
    }
    return fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    }).then(handleResponse);
  },

  // Import questionnaires - Step 2: Save selected questions to a questionnaire
  importSave: (questionnaireId, questions, assessmentId = null) => {
    return apiFetch('/Questionnaires/import/save', {
      method: 'POST',
      body: JSON.stringify({ questionnaireId, assessmentId, questions }),
    });
  },

  // Answer Options
  createAnswerOption: (questionnaireId, questionId, data) => apiFetch(`/Questionnaires/${questionnaireId}/questions/${questionId}/options`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  deleteAnswerOption: (questionnaireId, questionId, optionId) => apiFetch(`/Questionnaires/${questionnaireId}/questions/${questionId}/options/${optionId}`, {
    method: 'DELETE',
  }),

  // Assessment Answers
  getAnswers: (questionnaireId, assessmentId) => apiFetch(`/Questionnaires/${questionnaireId}/questions/answers?assessmentId=${assessmentId}`),

  getDocumentUrl: (questionnaireId, answerId) => apiFetch(`/Questionnaires/${questionnaireId}/questions/answers/${answerId}/document-url`),

  // Submit/update a reviewer answer
  submitAnswer: (questionnaireId, data) => apiFetch(`/Questionnaires/${questionnaireId}/questions/answers/submit`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Update review status for a single answer
  submitReview: (questionnaireId, data) => apiFetch(`/Questionnaires/${questionnaireId}/questions/answers/review`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Bulk update review status
  submitBulkReview: (questionnaireId, data) => apiFetch(`/Questionnaires/${questionnaireId}/questions/answers/review/bulk`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// ============ DASHBOARD API ============
export const dashboardApi = {
  getStats: () => apiFetch('/Dashboard/stats'),
  getTrustCenterStats: () => apiFetch('/Dashboard/trust-center-stats'),
  getAssessmentTrends: (months = 7) => apiFetch(`/Dashboard/assessment-trends?months=${months}`),
  getComplianceData: () => apiFetch('/Dashboard/compliance-data'),
  getRiskDistribution: () => apiFetch('/Dashboard/risk-distribution'),
  getUpcomingDeadlines: (count = 4) => apiFetch(`/Dashboard/upcoming-deadlines?count=${count}`),
  getTrends: (months = 6) => apiFetch(`/Dashboard/trends?months=${months}`),
  getCategories: () => apiFetch('/Dashboard/categories'),
  getActivity: (count = 10) => apiFetch(`/Dashboard/activity?count=${count}`),
  // Paginated activities with filtering
  getActivities: (page = 1, pageSize = 10, entityType = null, action = null, user = null, fromDate = null, toDate = null) => {
    const query = buildQueryString({ page, pageSize, entityType, action, user, fromDate, toDate });
    return apiFetch(`/Dashboard/activities?${query}`);
  },
};

// ============ RISKS API ============
export const risksApi = {
  getAll: (page = 1, pageSize = 10, category = null, severity = null, status = null) => {
    const query = buildQueryString({ page, pageSize, category, severity, status });
    return apiFetch(`/Risks?${query}`);
  },
  
  getById: (id) => apiFetch(`/Risks/${id}`),
  
  create: (data) => apiFetch('/Risks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiFetch(`/Risks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiFetch(`/Risks/${id}`, {
    method: 'DELETE',
  }),
};

// ============ CONTROLS API ============
export const controlsApi = {
  getAll: (page = 1, pageSize = 10, category = null, status = null) => {
    const query = buildQueryString({ page, pageSize, category, status });
    return apiFetch(`/Controls?${query}`);
  },
  
  getById: (id) => apiFetch(`/Controls/${id}`),
  
  create: (data) => apiFetch('/Controls', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiFetch(`/Controls/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiFetch(`/Controls/${id}`, {
    method: 'DELETE',
  }),
};

// ============ REPORTS API ============
export const reportsApi = {
  getAll: (page = 1, pageSize = 10, type = null) => {
    const query = buildQueryString({ page, pageSize, type });
    return apiFetch(`/Reports?${query}`);
  },
  
  generate: (data) => apiFetch('/Reports/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  download: (id) => apiFetch(`/Reports/${id}/download`),
  
  delete: (id) => apiFetch(`/Reports/${id}`, {
    method: 'DELETE',
  }),
};

// ============ TRUST CENTER API ============
// Dashboard-only endpoints. Assessment operations are in assessmentsApi.
export const trustCenterApi = {
  getDashboard: () => apiFetch('/TrustCenter/dashboard'),
  getQuestionnaires: (page = 1, pageSize = 10) => apiFetch(`/TrustCenter/questionnaires?page=${page}&pageSize=${pageSize}`),
  getDocuments: (page = 1, pageSize = 10) => apiFetch(`/TrustCenter/documents?page=${page}&pageSize=${pageSize}`),
  getActivity: (count = 10) => apiFetch(`/TrustCenter/activity?count=${count}`),
  getVendorCompliance: (page = 1, pageSize = 10) => apiFetch(`/TrustCenter/vendors?page=${page}&pageSize=${pageSize}`),
  // Deprecated: Use assessmentsApi.getMyAssessments instead
  getAssessments: (page = 1, pageSize = 10, status = null) => {
    const query = buildQueryString({ page, pageSize, status });
    return apiFetch(`/Assessments/my?${query}`);
  },
  // Deprecated: Use assessmentsApi.getMyAssessments instead
  getMyAssessments: (page = 1, pageSize = 10, status = null, organizationName = null, userId = null) => {
    const query = buildQueryString({ page, pageSize, status, organizationName, userId });
    return apiFetch(`/Assessments/my?${query}`);
  },
  // Deprecated: Use assessmentsApi.getSharedAssessmentsByEmail instead
  getSharedAssessmentsByEmail: (email, page = 1, pageSize = 10, status = null) => {
    const query = buildQueryString({ email, page, pageSize, status });
    return apiFetch(`/Assessments/shared?${query}`);
  },

  // ── Guest request submission (public, no auth required) ──────────────────
  submitRequest: (formData, files) => {
    const payload = new FormData();
    payload.append('fullName',     formData.fullName);
    payload.append('email',        formData.email);
    if (formData.organization && formData.organization !== 'other') {
      payload.append('organization', formData.organization);
    } else if (formData.organization === 'other' && formData.newOrg) {
      payload.append('organization', formData.newOrg);
    }
    payload.append('requestType',  formData.requestType);
    if (formData.queryAbout) payload.append('queryAbout', formData.queryAbout);
    if (formData.message)    payload.append('message',    formData.message);

    files.forEach((file) => payload.append('files', file));

    const url = `${API_BASE_URL}/TrustCenter/requests`;
    // No Content-Type header — browser sets it with boundary automatically
    return fetch(url, { method: 'POST', body: payload }).then(handleResponse);
  },

  getRequests: (page = 1, pageSize = 20, status = null) => {
    const query = buildQueryString({ page, pageSize, status });
    return apiFetch(`/TrustCenter/requests?${query}`);
  },

  getRequestById: (id) => apiFetch(`/TrustCenter/requests/${id}`),

  updateStatus: (id, status) => apiFetch(`/TrustCenter/requests/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  }),
};

// ============ SETTINGS API ============
export const settingsApi = {
  get: (userId = 1) => apiFetch(`/Settings/${userId}`),
  
  update: (userId = 1, data) => apiFetch(`/Settings/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  changePassword: (userId, data) => apiFetch(`/Settings/${userId}/change-password`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  getTimezones: () => apiFetch('/Settings/timezones'),
  
  getLanguages: () => apiFetch('/Settings/languages'),
};

// ============ SUPPORT API ============
export const supportApi = {
  // Tickets
  getTickets: (page = 1, pageSize = 10, status = null, category = null) => {
    const query = buildQueryString({ page, pageSize, status, category });
    return apiFetch(`/Support/tickets?${query}`);
  },
  
  getTicket: (id) => apiFetch(`/Support/tickets/${id}`),
  
  createTicket: (data) => apiFetch('/Support/tickets', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateTicketStatus: (id, status, resolution = null) => apiFetch(`/Support/tickets/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, resolution }),
  }),
  
  getTicketStats: () => apiFetch('/Support/tickets/stats'),
  
  // FAQs
  getFAQs: (category = null) => {
    const query = buildQueryString({ category });
    return apiFetch(`/Support/faqs?${query}`);
  },
  
  // Categories
  getCategories: () => apiFetch('/Support/categories'),
};

// ============ CROSSWALK API ============
export const crosswalkApi = {
  // Start a crosswalk job for a specific assessment + questionnaire
  startJob: (assessmentId, questionnaireId, minConfidence = 0, maxProposalsPerQuestion = 1) =>
    apiFetch('/Crosswalk/jobs', {
      method: 'POST',
      body: JSON.stringify({ assessmentId, questionnaireId, minConfidence, maxProposalsPerQuestion }),
    }),

  // Get crosswalk job status by job ID
  getJobStatus: (jobId) => apiFetch(`/Crosswalk/jobs/${jobId}`),

  // Trigger crosswalk for multiple assessments
  triggerForAssessments: (assessmentIds) =>
    apiFetch('/Crosswalk/trigger', {
      method: 'POST',
      body: JSON.stringify(assessmentIds),
    }),

  // Get proposals from DB (no job ID needed)
  getProposals: (assessmentId, questionnaireId) =>
    apiFetch(`/Crosswalk/proposals?assessmentId=${assessmentId}&questionnaireId=${questionnaireId}`),

  // Sync proposals into AssessmentAnswers table
  syncProposals: (assessmentId, questionnaireId) =>
    apiFetch('/Crosswalk/sync', {
      method: 'POST',
      body: JSON.stringify({ assessmentId, questionnaireId }),
    }),
};

export default {
  auth: authApi,
  assessments: assessmentsApi,
  organizations: organizationsApi,
  lookups: lookupsApi,
  users: usersApi,
  questionnaires: questionnairesApi,
  dashboard: dashboardApi,
  risks: risksApi,
  controls: controlsApi,
  reports: reportsApi,
  settings: settingsApi,
  support: supportApi,
  trustCenter: trustCenterApi,
  crosswalk: crosswalkApi,
};
